import {
  AlertTriangle,
  Ban,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Network,
  RefreshCw,
  Warehouse,
} from 'lucide-react'
import type { CSSProperties } from 'react'

import { Button, StatusMessage, TextField } from '@krp/ui-kit'

import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import '../../../shared/ui/onec.css'
import type { StructureFactVm, StructureNodeKind, StructureScreenActions, StructureScreenVm } from '../model/types'
import './structure.css'

/** Ширина пустого места вместо кнопки раскрытия у узла без потомков: подписи узлов остаются в одну линию. */
const TOGGLE_SPACER_SIZE = 40

const NODE_ICONS: Readonly<Record<StructureNodeKind, typeof Building2>> = {
  organization: Building2,
  department: Network,
  warehouse: Warehouse,
  other: FolderTree,
  group: AlertTriangle,
}

function FactIcon({ tone }: { tone: StructureFactVm['tone'] }) {
  if (tone === 'ok') return <CheckCircle2 size={14} aria-hidden="true" />
  if (tone === 'missing') return <AlertTriangle size={14} aria-hidden="true" />
  return <Ban size={14} aria-hidden="true" />
}

/**
 * «Структура 1С»: организация → подразделение → склад одной базы. Связь «не
 * задано в 1С» и связь, которой в конфигурации нет, различаются значком и
 * словами (не цветом). Структура читается из 1С на лету (RP-LSB2): над деревом —
 * когда прочитана; 1С не ответила (на чтение или на «Обновить из 1С») — её текст
 * и «Повторить» вместо дерева, прежняя структура не показывается.
 */
export function StructureScreen({ vm, actions }: { vm: StructureScreenVm; actions: StructureScreenActions }) {
  return (
    <section className="onec-screen" aria-labelledby="onec-structure-title">
      <header className="onec-toolbar">
        <h2 id="onec-structure-title">Структура 1С</h2>
        <Button
          variant="ghost"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          busy={vm.refreshing}
          onClick={actions.refresh}
        >
          Обновить из 1С
        </Button>
      </header>
      {vm.readAt !== null && (
        <p className="onec-freshness" data-kind="fresh" role="status">
          <CheckCircle2 size={14} aria-hidden="true" />
          <span>{vm.readAt}</span>
        </p>
      )}
      <OneCQueryState
        query={vm.query}
        emptyText="В этой базе нет организаций и структурных единиц"
        onRetry={vm.retry === 'refresh' ? actions.refresh : actions.retry}
      />
      {vm.limits.map((limit) => (
        <StatusMessage key={limit} kind="empty" title="Связи конфигурации" text={limit} />
      ))}
      {(vm.query.status === 'ready' || vm.query.status === 'empty') && vm.summary !== null && (
        <>
          <p className="onec-field__note">{vm.summary}</p>
          <TextField label="Найти в структуре" type="search" value={vm.search} onChange={actions.searchChanged} />
          <div className="onec-structure">
            {vm.nodes.length === 0 ? (
              <StatusMessage kind="empty" title="Ничего не найдено" />
            ) : (
              <ul className="onec-structure__tree" aria-label="Структура базы">
                {vm.nodes.map((node) => {
                  const Icon = NODE_ICONS[node.kind]
                  return (
                    <li
                      key={node.id}
                      className="onec-structure__node"
                      data-selected={node.selected}
                      style={{ '--depth': node.depth } as CSSProperties}
                    >
                      {node.expandable ? (
                        <Button
                          variant="ghost"
                          aria-expanded={node.expanded}
                          aria-label={`${node.expanded ? 'Свернуть' : 'Развернуть'}: ${node.name}`}
                          icon={
                            node.expanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />
                          }
                          onClick={() => actions.toggled(node.id)}
                        />
                      ) : (
                        <span style={{ inlineSize: TOGGLE_SPACER_SIZE }} aria-hidden="true" />
                      )}
                      <span className="onec-structure__label">
                        <button
                          type="button"
                          className="onec-structure__name"
                          aria-pressed={node.selected}
                          onClick={() => actions.selected(node.id)}
                        >
                          <Icon size={16} aria-hidden="true" />
                          <span>{node.name}</span>
                        </button>
                        {node.caption !== null && <span className="onec-muted">{node.caption}</span>}
                        {(node.kind === 'organization' || node.kind === 'department' || node.kind === 'group') && (
                          <span className="onec-muted">· складов: {node.warehouses}</span>
                        )}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
            <aside aria-label="Карточка элемента структуры">
              {vm.details === null ? (
                <p className="onec-field__note">Выберите элемент структуры, чтобы увидеть его связи.</p>
              ) : (
                <>
                  <h3>{vm.details.name}</h3>
                  <dl className="onec-structure__facts">
                    {vm.details.facts.map((fact) => (
                      <div key={fact.label} style={{ display: 'contents' }}>
                        <dt>{fact.label}</dt>
                        <dd data-tone={fact.tone}>
                          <FactIcon tone={fact.tone} />
                          {fact.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
            </aside>
          </div>
        </>
      )}
    </section>
  )
}
